import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

const FilePreviewCtx = createContext(null);

function fileEndpoint(kind, id, page) {
  if (kind === "document") return `/drivers/documents/${id}/file`;
  if (kind === "page") return `/drivers/documents/${id}/pages/${page}/file`;
  if (kind === "agreement") return `/agency/agreements/${id}/pdf`;
  if (kind === "url") return id;
  return `/drivers/maintenance/${id}/file`;
}

/**
 * Slide-over file viewer state: which file is open, its fetched blob, and the loading/error
 * status. Two independent instances exist in the app (mounted in Layout.jsx for the agency
 * console, and in DriverPortal.jsx for the driver shell) since those two route trees never
 * share a common ancestor. See FilePreviewDrawer.jsx for the component that renders this state.
 */
export function FilePreviewProvider({ children }) {
  const [entry, setEntry] = useState(null); // { kind, id, page, meta } | null
  const [status, setStatus] = useState("idle"); // idle | loading | ready | error
  const [blobUrl, setBlobUrl] = useState(null);
  const [contentType, setContentType] = useState(null);
  const blobUrlRef = useRef(null);
  const requestIdRef = useRef(0);

  const revoke = useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, []);

  const fetchFile = useCallback(async (kind, id, fallbackContentType, page) => {
    const requestId = ++requestIdRef.current;
    setStatus("loading");
    revoke();
    setBlobUrl(null);
    try {
      const r = await api.get(fileEndpoint(kind, id, page), { responseType: "blob" });
      if (requestId !== requestIdRef.current) return; // a newer openPreview superseded this one
      const url = URL.createObjectURL(r.data);
      blobUrlRef.current = url;
      setBlobUrl(url);
      setContentType(r.data.type || r.headers?.["content-type"] || fallbackContentType || "");
      setStatus("ready");
    } catch {
      if (requestId !== requestIdRef.current) return;
      setStatus("error");
    }
  }, [revoke]);

  // kind: "document" (a driver_documents row), "page" (an appended page of one, needs `page`
  // as the page number), or "maintenance" (a vehicle_maintenance receipt).
  const openPreview = useCallback(({ kind, id, page, meta }) => {
    setEntry({ kind, id, page: page ?? null, meta: meta || {} });
    fetchFile(kind, id, meta?.content_type, page);
  }, [fetchFile]);

  const closePreview = useCallback(() => {
    requestIdRef.current += 1; // orphan any in-flight fetch
    setEntry(null);
    setStatus("idle");
    revoke();
    setBlobUrl(null);
    setContentType(null);
  }, [revoke]);

  const retry = useCallback(() => {
    if (entry) fetchFile(entry.kind, entry.id, entry.meta?.content_type, entry.page);
  }, [entry, fetchFile]);

  // Patch the currently-open file's header fields (e.g. after PATCH expires_at) without a refetch.
  const updateMeta = useCallback((patch) => {
    setEntry((s) => (s ? { ...s, meta: { ...s.meta, ...patch } } : s));
  }, []);

  useEffect(() => () => revoke(), [revoke]);

  const value = {
    isOpen: !!entry,
    kind: entry?.kind || null,
    id: entry?.id || null,
    page: entry?.page ?? null,
    meta: entry?.meta || null,
    status,
    blobUrl,
    contentType,
    openPreview,
    closePreview,
    retry,
    updateMeta,
  };

  return <FilePreviewCtx.Provider value={value}>{children}</FilePreviewCtx.Provider>;
}

export function useFilePreview() {
  const ctx = useContext(FilePreviewCtx);
  if (!ctx) throw new Error("useFilePreview must be called inside a FilePreviewProvider");
  return ctx;
}
